import { useEffect, useMemo, useState } from 'react';
import { flightStatusApi } from '@/lib/flightStatusApi';
import { tripMightBeLive } from '@/utils/flightStatusDisplay';
import { useIsMounted } from '@/hooks/useIsMounted';

/** How often an open screen re-asks while a flight is live. */
const LIVE_REFRESH_MS = 2 * 60 * 1000;
const INSIGHTS_REFRESH_MS = 10 * 60 * 1000;
const ACTIVE_TRIP_STATUSES = new Set(['confirmed', 'completed', 'delinquent']);

const hasLiveFlight = (segments) => (segments || []).some((s) => s.status && !['arrived', 'cancelled'].includes(s.status.state));

/**
 * Live status for the Trips list: one request for every trip with a flight
 * within a couple of days, refreshed every 2 minutes while one is in the air
 * or about to go. Returns { [bookingId]: segments }.
 */
export function useTripsFlightStatus(trips) {
  const isMounted = useIsMounted();
  const [byBooking, setByBooking] = useState({});
  const ids = useMemo(() => {
    const out = [];
    for (const trip of trips || []) {
      if (!tripMightBeLive(trip)) continue;
      for (const booking of trip) {
        if (booking?.id && ACTIVE_TRIP_STATUSES.has(String(booking.status || '').toLowerCase())) out.push(booking.id);
      }
    }
    return [...new Set(out)].slice(0, 20);
  }, [trips]);
  const key = ids.join(',');

  useEffect(() => {
    if (!ids.length) return undefined;
    let timer = null;
    const load = async () => {
      try {
        const data = await flightStatusApi.batch(ids);
        if (!isMounted()) return;
        const next = {};
        for (const [id, value] of Object.entries(data?.bookings || {})) next[id] = value.segments || [];
        setByBooking(next);
        if (Object.values(next).some(hasLiveFlight)) timer = setTimeout(load, LIVE_REFRESH_MS);
      } catch {
        // Status is extra; the list works without it.
      }
    };
    load();
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `ids`
  }, [key]);

  return byBooking;
}

/**
 * Live status and insights for one booking, for the trip page. Status
 * refreshes every 2 minutes while a flight is live; insights every 10.
 */
export function useFlightLive(bookingId, { enabled = true } = {}) {
  const isMounted = useIsMounted();
  const [status, setStatus] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    if (!enabled || !bookingId) return undefined;
    let statusTimer = null;
    let insightsTimer = null;
    const loadStatus = async () => {
      try {
        const data = await flightStatusApi.status(bookingId);
        if (!isMounted()) return;
        setStatus(data);
        if (data?.enabled && hasLiveFlight(data.segments)) statusTimer = setTimeout(loadStatus, LIVE_REFRESH_MS);
      } catch {
        // Nothing to show.
      }
    };
    const loadInsights = async () => {
      try {
        const data = await flightStatusApi.insights(bookingId);
        if (!isMounted()) return;
        setInsights(data);
        if (data?.enabled) insightsTimer = setTimeout(loadInsights, INSIGHTS_REFRESH_MS);
      } catch {
        // Nothing to show.
      }
    };
    loadStatus();
    loadInsights();
    return () => {
      if (statusTimer) clearTimeout(statusTimer);
      if (insightsTimer) clearTimeout(insightsTimer);
    };
  }, [bookingId, enabled, isMounted]);

  return { status, insights };
}
